import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { getHebrewYear } from '../../../../../../../../../utils/hebrew-date-utils';

export type StudyCertificateProgramNameChoice =
  | 'programName'
  | 'additionalProgramName';

export type StudyCertificateExportFormat = 'pdf' | 'word';

export interface StudyCertificateDialogConfirmPayload {
  programNameChoice: StudyCertificateProgramNameChoice;
  selectedYear: number;
  format: StudyCertificateExportFormat;
}

interface StudyCertificateProgramNameDialogProps {
  visible: boolean;
  programName: string;
  additionalProgramName?: string | null;
  programStartYear: number;
  programYears: number;
  onConfirm: (payload: StudyCertificateDialogConfirmPayload) => void;
  onCancel: () => void;
}

const buildProgramYearOptions = (
  programStartYear: number,
  programYears: number,
): { label: string; value: number }[] => {
  const endYear = programStartYear + programYears;
  const options: { label: string; value: number }[] = [];

  for (let year = programStartYear; year <= endYear; year++) {
    options.push({
      label: getHebrewYear(year),
      value: year,
    });
  }

  return options;
};

export const StudyCertificateProgramNameDialog: React.FC<
  StudyCertificateProgramNameDialogProps
> = ({
  visible,
  programName,
  additionalProgramName,
  programStartYear,
  programYears,
  onConfirm,
  onCancel,
}) => {
  const [selectedChoice, setSelectedChoice] =
    useState<StudyCertificateProgramNameChoice>('programName');
  const [selectedYear, setSelectedYear] = useState<number>(programStartYear);

  const yearOptions = useMemo(
    () => buildProgramYearOptions(programStartYear, programYears),
    [programStartYear, programYears],
  );

  useEffect(() => {
    if (visible) {
      setSelectedChoice('programName');
      setSelectedYear(programStartYear);
    }
  }, [visible, programStartYear]);

  const handleConfirm = (format: StudyCertificateExportFormat) => {
    onConfirm({
      programNameChoice: selectedChoice,
      selectedYear,
      format,
    });
  };

  const hasProgramNameChoice = !!additionalProgramName;

  return (
    <Dialog
      header="בחירת שם תוכנית למסמך"
      visible={visible}
      style={{ width: '450px' }}
      onHide={onCancel}
      dismissableMask
      draggable={false}
      resizable={false}
    >
      <div style={{ padding: '8px 0' }}>
        {hasProgramNameChoice && (
          <>
            <p
              style={{
                fontSize: '14px',
                color: '#4b5563',
                marginBottom: '16px',
              }}
            >
              בחר את שם התוכנית שיופיע באישור הלימודים:
            </p>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '24px',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <RadioButton
                  inputId="study-certificate-program-name"
                  name="studyCertificateProgramName"
                  value="programName"
                  onChange={(e) =>
                    setSelectedChoice(
                      e.value as StudyCertificateProgramNameChoice,
                    )
                  }
                  checked={selectedChoice === 'programName'}
                />
                <label
                  htmlFor="study-certificate-program-name"
                  style={{ cursor: 'pointer' }}
                >
                  {programName}
                </label>
              </div>

              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <RadioButton
                  inputId="study-certificate-additional-program-name"
                  name="studyCertificateProgramName"
                  value="additionalProgramName"
                  onChange={(e) =>
                    setSelectedChoice(
                      e.value as StudyCertificateProgramNameChoice,
                    )
                  }
                  checked={selectedChoice === 'additionalProgramName'}
                />
                <label
                  htmlFor="study-certificate-additional-program-name"
                  style={{ cursor: 'pointer' }}
                >
                  {additionalProgramName}
                </label>
              </div>
            </div>
          </>
        )}

        <div style={{ marginBottom: '24px' }}>
          <div
            style={{ fontSize: '14px', color: '#4b5563', marginBottom: '4px' }}
          >
            שנת לימודים
          </div>
          <Dropdown
            value={selectedYear}
            onChange={(e: DropdownChangeEvent) => setSelectedYear(e.value)}
            options={yearOptions}
            placeholder="בחר שנת לימודים"
            style={{
              height: '35px',
              alignItems: 'center',
              width: '100%',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <Button
            label="PDF"
            icon="pi pi-file-pdf"
            severity="danger"
            onClick={() => handleConfirm('pdf')}
          />
          <Button
            label="Word"
            icon="pi pi-file-word"
            severity="info"
            onClick={() => handleConfirm('word')}
          />
          <Button
            label="ביטול"
            icon="pi pi-times"
            className="p-button-secondary"
            onClick={onCancel}
          />
        </div>
      </div>
    </Dialog>
  );
};
